// ─── homedepotAgent.js ────────────────────────────────────────────────────────
// Real agent for Home Depot Affiliate API (CJ feed, via Anthropic mock).
// Outputs { item, brand, price, rating, inStock, store } + HD extras.
// ─────────────────────────────────────────────────────────────────────────────

// Calls our local proxy server (server.js) which forwards to Anthropic.
// The API key stays on the server — never exposed to the browser.
const ANTHROPIC_API = '/api/anthropic';
const MODEL         = 'claude-haiku-4-5-20251001';

function buildPrompt(query) {
  return `Return ONLY a raw JSON object. No markdown, no explanation, no extra text.

Simulate a Home Depot Affiliate API (CJ feed) search response for: "${query}"

Return exactly 3 products using this schema:
{
  "items": [
    {
      "title": "Full product title",
      "brand": "Brand name",
      "price": 149.00,
      "original_price": 179.00,
      "rating": 4.6,
      "review_count": 890,
      "model_number": "DCD777C2",
      "hd_sku": "123456789",
      "internet_number": "123456",
      "bulk_price": 0,
      "bulk_threshold": 0,
      "unit_of_measure": "Each",
      "shipping_free": true,
      "shipping_cost": 0,
      "store_count": 1200,
      "availability": "In Stock"
    }
  ]
}

Rules:
- REAL HD product names, brands, and realistic prices for "${query}"
- Brands: DEWALT, Milwaukee, Ryobi, Makita, Ridgid, Husky, Bosch, Eaton, Leviton, PPG
- model_number: real manufacturer format (e.g. DCD777C2, 2504-20, WR15X10097)
- hd_sku: 9-digit Home Depot SKU
- bulk_price + bulk_threshold: set for 1 item if applicable (e.g. lumber, fasteners) — bulk_price lower than price, threshold 4–10; set 0 for others
- unit_of_measure: "Each" for tools, "Linear Ft" for lumber, "Sq. Ft." for flooring, "Gallon" for paint
- shipping_free: true if price >= 45
- store_count: 800–2000
- original_price higher than price for 2 items, same for 1
- Price at low / mid / premium tiers for "${query}"`;
}

async function callAPI(prompt) {
  const response = await fetch(ANTHROPIC_API, {
    method:  'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model:      MODEL,
      max_tokens: 2000,
      messages:   [{ role: 'user', content: prompt }],
    }),
  });
  if (!response.ok) throw new Error(`Home Depot agent API error: ${response.status}`);
  const data    = await response.json();
  const rawText = data.content?.find(b => b.type === 'text')?.text || '';
  const first   = rawText.indexOf('{');
  const last    = rawText.lastIndexOf('}');
  if (first === -1) throw new Error('Home Depot agent: no JSON found');
  return JSON.parse(rawText.slice(first, last + 1));
}

function normalizeItem(raw, store) {
  const price         = parseFloat(raw.price)          || 0;
  const originalPrice = parseFloat(raw.original_price) || null;
  const discountPct   = originalPrice && originalPrice > price
    ? Math.round((1 - price / originalPrice) * 100) : null;
  const bulkPrice     = parseFloat(raw.bulk_price)     || null;
  const bulkThreshold = parseInt(raw.bulk_threshold)   || null;
  const hasBulk       = !!(bulkPrice && bulkThreshold && bulkPrice < price);

  return {
    item:           raw.title           || 'Unknown Product',
    brand:          raw.brand           || 'Unknown',
    price,
    rating:         parseFloat(raw.rating) || 4.0,
    inStock:        raw.availability    !== 'Out of Stock',
    store:          store.id,

    // Home Depot-specific extras
    model_number:   raw.model_number    || null,
    hd_sku:         raw.hd_sku          || null,
    internet_number:raw.internet_number || null,
    url:            raw.url             || `https://www.homedepot.com/s/${encodeURIComponent(raw.title||'')}`,
    original_price: originalPrice,
    discount_pct:   discountPct,
    review_count:   parseInt(raw.review_count) || 0,
    unit_of_measure:raw.unit_of_measure || 'Each',
    bulk_price:     bulkPrice,
    bulk_threshold: bulkThreshold,
    has_bulk_discount: hasBulk,
    bulk_display:   hasBulk ? `$${bulkPrice.toFixed(2)} ea. when you buy ${bulkThreshold}+` : null,
    shipping_free:  raw.shipping_free   === true,
    shipping_cost:  parseFloat(raw.shipping_cost) || 0,
    store_count:    parseInt(raw.store_count) || 0,
    total_cost:     +(price + (raw.shipping_free ? 0 : (parseFloat(raw.shipping_cost) || 0))).toFixed(2),
    marketplace:    'homedepot',
  };
}

export async function runHomedepotAgent(store, query) {
  const prompt = buildPrompt(query);
  const parsed = await callAPI(prompt);
  const items  = Array.isArray(parsed.items) ? parsed.items : [];
  return items.map(item => normalizeItem(item, store));
}

export default runHomedepotAgent;
